import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import {
  apiSignUp,
  apiSendOtp,
  apiVerifyOtp,
  apiSkipVerification,
  apiCanceledSignupDeleteUserRecord,
  apiAutoSignIn,
  apiVerifyUserByPhoneNumber,
  apiClearOtpToken,
  apiVerifyOtpWhileLoggedIn,
} from "../../api/backend/auth.js";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator.js";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes";
import { formatPhoneNumber } from "../../utils/formatNumber.js";
// wherever you keep your cookie utils
import { clearCookie, MEMBER_ID_COOKIE_NAME } from "../../utils/cookies";

import {
  LoginForm,
  SignupForm,
  OtpPhoneVerificationForm,
  AccountVerificationPhoneNumberFieldForm,
  ResetPasswordForm,
  BuildProfileSignupForm,
} from "./index";

const AuthorizationForm = ({ onMemberCancelled, onMemberVerified }) => {
  // ─── single source of truth ───────────────────────────────────────────────
  const navigate = useNavigate();
  const verifyMemberRequiredRef = useRef(false);
  // authorizationForm.jsx
  const sendOnceRef = useRef(false); // send OTP exactly once per entry
  const isVerifyingRef = useRef(false); // suppress auto-send while VERIFY is in-flight

  // currentMemberId is not “global” in the window‐sense, but yes—any variable I pull out via hooks at the top of any component
  // component (like const { currentMemberId } = useAuth()) lives in the closure for the entire render
  // of that component. Every handler or helper declared inside that function body can just
  // reference currentMemberId without having to pass it as an argument.
  const {
    currentMemberId,
    setCurrentMemberId,
    loginMemberApproved,
    currentMemberData,
    setCurrentMemberData,
    setRemoteColor,
    currentFormStep,
    setCurrentFormStep,
    currentContextType,
    setCurrentContextType,
    setAuthReady,
  } = useAuth();
  // — Signup intermediary and setting it locally to avoid the re-renders from the call back in context or the auth provider that wraps the app in the index —
  const [pendingMemberId, setPendingMemberId] = useState(null);
  const [acctPhoneNumberBackendError, setAcctPhoneNumberBackendError] =
    useState("");
  const [errors, setErrors] = useState({});
  const [acctPhoneNumberValue, setAcctPhoneNumberValue] = useState(
    localStorage.getItem(
      getStorageKey("verifyAccountValue", currentContextType)
    ) || ""
  );
  const getCurrentUserId = () => pendingMemberId || currentMemberId; // the other functions don't have race conditions issue in the flow so setting the props globally here and it does not

  const autoSignIn = async (passingInUserId, passingInUserObject) => {
    console.log("autoSignIn called, userId:", passingInUserId);
    if (!passingInUserId) {
      console.warn("autoSignIn aborted: no userId");
      return Promise.resolve();
    }

    // hard reset at start of flow so NotificationProvider stays gated
    setAuthReady(false);

    return apiAutoSignIn({ userId: passingInUserId })
      .then((res) => {
        const ok = res?.status === 200 && res?.data?.tokenApproved === true;
        if (!ok) {
          throw new Error(res?.data?.message || "Token not approved");
        }
        return res;
      })
      .then(() => {
        // this is where your cookie is set (per your logs)
        console.log("Token approved → Calling loginMemberApproved...");
        return loginMemberApproved(passingInUserId);
      })
      .then(() => {
        // NOW you’re truly ready for any authed calls like notifications
        setAuthReady(true);

        if (passingInUserObject) {
          setCurrentMemberData(passingInUserObject);
          localStorage.setItem("remoteColor", passingInUserObject.remoteColor);
          setRemoteColor(passingInUserObject.remoteColor);
        }

        Object.values(FORM_FLOW_CONTROL).forEach((ct) =>
          [
            "acctPhoneNumberValue",
            "userNameValue",
            "verifyAccountValue",
            "emailValue",
            "passwordValue",
            "credentialChoice",
            "usernameReferral",
            "phoneNumberValue",
            "confirmPasswordValue",
            "currentPasswordValue",
          ].forEach((key) => localStorage.removeItem(getStorageKey(key, ct)))
        );

        console.log("Cleared temp form data");
        console.log("Navigating to /skitslist");
        navigate("/skitslist");
        setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
      })
      .catch((err) => {
        setAuthReady(false);
        console.error("autoSignIn failed:", err);
      });
  };

  const handleSignupUser = async ({
    acctPhoneNumberValue,
    userNameValue,
    currentPasswordValue,
  }) => {
    const phoneNumberWithPrefix = formatPhoneNumber(acctPhoneNumberValue);
    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_UP_FORM);
    try {
      const response = await apiSignUp({
        phoneNumber: phoneNumberWithPrefix,
        username: userNameValue,
        password: currentPasswordValue,
      });
      const { data } = response;

      if (data.status === "OK" && data.user) {
        // Store the new user ID locally
        setPendingMemberId(data.user.id);
        setCurrentMemberData(data.user);
        // Kick off the OTP flow for phone verification
        await handleMobileVerification(data.user.id, null, {
          passingUserData: data.user,
          currentContextType: FORM_FLOW_CONTROL.SIGN_UP_FORM,
        });

        // Advance to the OTP verification step
        setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
      } else {
        console.error("Unexpected signup status:", data);
        setErrors({ signup: data.message || "Signup failed" });
      }
    } catch (error) {
      console.error("Signup exception:", error);
      setErrors({ signup: error.message || "Server error" });
    }
  };

  const handleAccountVerification = async (acctPhoneNumberValue) => {
    setAcctPhoneNumberBackendError("");
    setCurrentContextType(FORM_FLOW_CONTROL.RESET_PASSWORD_FORM);
    setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
    const phoneNumberWithPrefix = formatPhoneNumber(acctPhoneNumberValue);
    try {
      // 1) Verify user existence by phone number only
      const { data } = await apiVerifyUserByPhoneNumber(phoneNumberWithPrefix);
      const user = data.user;
      setCurrentMemberId(user.id);
      setCurrentMemberData(user);
      // 2) Call handleMobileVerification here WITHOUT OTP code to send OTP
      await handleMobileVerification(user.id, null, {
        passingUserData: user,
        currentContextType: FORM_FLOW_CONTROL.RESET_PASSWORD_FORM,
      });
    } catch (errors) {
      setAcctPhoneNumberBackendError(
        errors.response?.status === 404 ? "User not found" : "Server error"
      );
    }
  };

  const performSkipVerification = async (e) => {
    e.preventDefault();
    const userId = getCurrentUserId();
    if (!userId) {
      console.error("No userId to skip for");
      return;
    }
    try {
      const { data } = await apiSkipVerification(userId);
      if (data.status !== "OK") {
        console.error("skip-verification failed on the server:", data);
        return;
      }
      // server should return updated user object
      const updatedUser = data.user;
      // (optionally stash them in context)
      // RIGHT: pass the whole user object
      setCurrentMemberData(updatedUser);
      setCurrentMemberId(updatedUser.id);

      setCurrentContextType(FORM_FLOW_CONTROL.SIGN_UP_FORM);
      setCurrentFormStep(FORM_FLOW_CONTROL.BUILD_PROFILE_FORM);
    } catch (err) {
      console.error("Skip failed:", err);
    }
  };

  const handleLoginSuccess = async (passingUserDataIn) => {
    if (!passingUserDataIn) {
      console.error("No user!");
      return;
    }

    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    setCurrentMemberData(passingUserDataIn);
    setCurrentMemberId(passingUserDataIn.id);

    // “hasAuth” if *either* field is not null/undefined
    const hasAuth =
      passingUserDataIn.authToken != null ||
      passingUserDataIn.authTokenExpiresAt != null;

    // “hasPhone” if it’s a non-empty string
    const hasPhone =
      typeof passingUserDataIn.phoneNumber === "string" &&
      passingUserDataIn.phoneNumber.length > 0;

    if (hasAuth && hasPhone) {
      try {
        setCurrentContextType(FORM_FLOW_CONTROL.SECURITY_CHECK);
        await handleMobileVerification(passingUserDataIn.id, null, {
          passingUserData: passingUserDataIn,
          currentContextType: FORM_FLOW_CONTROL.SECURITY_CHECK,
        });
        setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
        return;
      } catch (err) {
        console.error("OTP send failed:", err);
      }
    }
    // otherwise skip straight to completion
    await completeSignup(passingUserDataIn, FORM_FLOW_CONTROL.SIGN_IN_FORM);
  };

  const handleResend = async () => {
    const userId = getCurrentUserId();
    const userData = currentMemberData;
    // Guard: make sure we still have a phone number to send to
    if (!userId || !userData?.phoneNumber) {
      console.error("handleResend: missing userId or phoneNumber", {
        userId,
        phoneNumber: userData?.phoneNumber,
      });
      setErrors({ otp: "Unable to resend OTP. Please restart the flow." });
      return;
    }
    try {
      // Just call your existing OTP‐send logic with no code
      await handleMobileVerification(userId, null, {
        passingUserData: userData,
        currentContextType,
      });
      // Optionally clear any OTP‐specific errors
      setErrors((errors) => ({ ...errors, otp: null }));
    } catch (err) {
      console.error("handleResend error:", err);
      setErrors({ otp: err.message || "Failed to resend OTP" });
    }
  };

  useEffect(() => {
    if (
      currentContextType !==
      FORM_FLOW_CONTROL.MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS
    ) {
      sendOnceRef.current = false; // leaving the gate → allow future triggers again
      isVerifyingRef.current = false;
      return;
    }

    console.log("[AuthForm] ➜ Upload gate detected", {
      currentMemberId,
      hasPhone: !!currentMemberData?.phoneNumber,
      step: currentFormStep,
    });

    if (!currentMemberId || !currentMemberData?.phoneNumber) return;

    if (sendOnceRef.current) {
      console.log("[AuthForm] bail: one-shot already triggered");
      return;
    }
    if (isVerifyingRef.current) {
      console.log("[AuthForm] user is verifying → do not auto resend");
      return;
    }

    if (currentFormStep !== FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM) {
      console.log("[AuthForm] setCurrentFormStep → VERIFY_ACCOUNT_FORM");
      setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
      isVerifyingRef.current = true; // we are now in the verify UI
    }

    console.log("[AuthForm] upload gate → invoking keeper…");
    sendOnceRef.current = true; // avoid double fire
    handleResend(); // sends OTP; no callback here
  }, [
    currentContextType,
    currentMemberId,
    currentMemberData,
    currentFormStep,
    setCurrentFormStep,
  ]);

  const handleMobileVerification = async (
    _userId,
    _otpCode,
    { passingUserData, currentContextType }
  ) => {
    console.log("↪ enter handleMobileVerification", {
      _userId,
      _otpCode,
      passingUserData,
      currentContextType,
    });
    const phoneNumber = passingUserData?.phoneNumber;
    const userId = passingUserData?.id;
    if (!phoneNumber || !userId) {
      console.error("Missing userId or phoneNumber:", { userId, phoneNumber });
      return;
    }
    // — SEND (or RESEND) OTP —
    if (!_otpCode) {
      console.log("  • branch: SEND OTP");
      try {
        console.log(`${userId} Sending OTP to`, phoneNumber);
        const resp = await apiSendOtp({ phoneNumber });
        console.log(`${phoneNumber} sendOtp response:`, resp);
      } catch (err) {
        console.error(`${phoneNumber} Error sending OTP:`, err);
      }
      return; // don't fall through to VERIFY branch
    }
    // — VERIFY OTP (only when an _otpCode is provided) —
    try {
      console.log("  • branch: VERIFY OTP", {
        userId: _userId,
        otpCode: _otpCode,
      });
      // Use while-logged-in endpoint ONLY for these two lanes
      const useWhileLoggedInAPI =
        currentContextType === FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED ||
        currentContextType ===
          FORM_FLOW_CONTROL.MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS;

      const verifyFn = useWhileLoggedInAPI
        ? apiVerifyOtpWhileLoggedIn
        : apiVerifyOtp;
      const { data } = await verifyFn({ userId: _userId, otpCode: _otpCode });
      if (!data || data.status !== "OK") {
        const msg = data?.message || "OTP verification failed";
        console.error("[OTP] Verification failed:", msg);
        throw new Error(msg);
      }
      const verifiedUser = data.user;
      console.log("[OTP] verified", {
        lane: currentContextType,
        hasToken: !!verifiedUser?.authToken,
        expiresAt: verifiedUser?.authTokenExpiresAt,
      });
      // (Legacy crash-state cleanup: should be false for while-logged-in flow)
      if (data.authCleared) {
        console.log("[OTP] backend cleared auth; syncing local cache");
        clearCookie(MEMBER_ID_COOKIE_NAME);
        localStorage.removeItem("currentMemberData");
      }
      // Keep local auth cache in sync
      setCurrentMemberData(verifiedUser);
      setCurrentMemberId(verifiedUser.id);
      isVerifyingRef.current = false; // we finished verifying
      // ── decide path; common tail runs after switch ────────────────────────
      if (currentContextType === FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED) {
        console.log("[OTP] lane=PROFILE/CAMPAIGN → notify parent only");
        onMemberVerified?.(verifiedUser);
        return;
      }

      // MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS (and any other gates)
      console.log(
        "[OTP] common tail → completeSignup(ctx=%s)",
        currentContextType
      );
      await completeSignup(verifiedUser, currentContextType);
      return;
    } catch (err) {
      console.error("[OTP] Error verifying code:", err);
      const msg =
        err?.response?.data?.message || err?.message || "Error verifying code";
      throw new Error(msg);
    }
  };

  useEffect(() => {
    if (currentContextType !== FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED) {
      verifyMemberRequiredRef.current = false;
      return;
    }
    if (currentFormStep !== FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM) {
      setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
    }
    if (
      !verifyMemberRequiredRef.current &&
      getCurrentUserId() &&
      currentMemberData?.phoneNumber
    ) {
      verifyMemberRequiredRef.current = true;
      handleResend();
    }
  }, [
    currentContextType,
    currentFormStep,
    currentMemberData,
    setCurrentFormStep,
  ]);

  const completeSignup = async (passingUserDataIn, currentContextType) => {
    if (!passingUserDataIn?.id) {
      console.error(
        "completeSignup called with invalid user:",
        passingUserDataIn
      );
      return;
    }
    const isPhoneVerified = !!passingUserDataIn?.phoneNumberVerified;
    const isProfileComplete =
      !!passingUserDataIn?.firstName &&
      !!passingUserDataIn?.lastName &&
      !!passingUserDataIn?.email;
    console.log("Running completeSignup:", {
      isPhoneVerified,
      isProfileComplete,
      currentContextType,
    });
    setCurrentMemberData(passingUserDataIn);
    setCurrentMemberId(passingUserDataIn.id);
    // 🔹 Step 1: If profile is incomplete, go build it FROM ANY FLOW
    if (!isProfileComplete) {
      console.warn("[completeSignup] INCOMPLETE_PROFILE → BUILD_PROFILE_FORM", {
        ctx: currentContextType,
        userId: passingUserDataIn.id,
        firstName: !!passingUserDataIn.firstName,
        lastName: !!passingUserDataIn.lastName,
        email: !!passingUserDataIn.email,
        at: new Date().toISOString(),
      });
      console.trace(
        "[completeSignup] setCurrentFormStep(BUILD_PROFILE_FORM) stack"
      );
      setCurrentFormStep(FORM_FLOW_CONTROL.BUILD_PROFILE_FORM);
      return;
    }
    if (
      currentContextType ===
      FORM_FLOW_CONTROL.MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS
    ) {
      if (isProfileComplete) {
        onMemberVerified?.(passingUserDataIn);
        return;
      }
    }
    // 🔹 Step 2: If resetting password phone was verified before here so if the profile is complete they can go reset the password
    // from there the user can auto signin when the on onUpdateAccount call back prop is invoked, will call the autoSignIn function
    if (currentContextType === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM) {
      setCurrentFormStep(FORM_FLOW_CONTROL.RESET_PASSWORD_FORM);
      return;
    }
    // 🔹 Step 3: Phone must be verified first before the member can sign in
    if (
      currentContextType === FORM_FLOW_CONTROL.SIGN_IN_FORM &&
      !isPhoneVerified
    ) {
      // ① send the very first code, also handles the veryify api call
      await handleMobileVerification(passingUserDataIn.id, null, {
        passingUserData: passingUserDataIn,
        currentContextType,
      });
      // ② asyncronously render the otp form so the member can put the code in the form field
      setCurrentFormStep(FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM);
      return;
    }

    // 🔹 Step 5: user attributes pass for sign in and signup — auto sign in
    if (
      currentContextType === FORM_FLOW_CONTROL.SIGN_IN_FORM ||
      currentContextType === FORM_FLOW_CONTROL.SIGN_UP_FORM ||
      currentContextType === FORM_FLOW_CONTROL.SECURITY_CHECK
    ) {
      autoSignIn(passingUserDataIn.id, passingUserDataIn);
      return;
    }
  };

  const cancelSignupFlow = async (e, setLoading) => {
    e.preventDefault();
    setLoading(true); // ← spinner starts in the child
    try {
      console.log("Cancel button clicked");
      const userId = pendingMemberId;
      localStorage.clear();
      setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
      setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
      if (userId) {
        const response = await apiCanceledSignupDeleteUserRecord(userId);
        console.log("Cancel API Response:", response);
        if (response.data.status !== "OK") {
          console.error("Failed to delete user record:", response.data);
        }
      }
    } catch (error) {
      console.error("Error canceling the signup process:", error);
    } finally {
      setLoading(false); // ← spinner stops in the child
    }
  };

  const cancelOtp = async (e, setLoading) => {
    e.preventDefault();
    setLoading(true); // ← start spinner in the child
    try {
      // call the API and grab the full response
      const response = await apiClearOtpToken(getCurrentUserId());
      // only treat a 200/"OK" as success
      if (response.status === 200 && response.data.status === "OK") {
        console.log("OTP tokens cleared on server");
      } else {
        console.error("Cancel OTP failed:", response.data);
      }
    } catch (err) {
      console.error("Failed to cancel OTP on server:", err);
    } finally {
      // regardless of success, clear local state & navigate home
      localStorage.clear();
      setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
      setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
      setLoading(false); // ← stop spinner in the child
    }
  };

  // 🔻 Put inside AuthorizationForm
  const handleContextAwareCancel = async (e, setLoading) => {
    e?.preventDefault?.();
    setLoading?.(true);
    const closeHost = async () => {
      try {
        await apiClearOtpToken(getCurrentUserId()); // idempotent; fine to call even if no OTP exists
      } finally {
        setLoading?.(false); // stop spinner
      }
    };
    try {
      switch (currentContextType) {
        // combine cases by stacking them
        case FORM_FLOW_CONTROL.RESET_PASSWORD_FORM:
        case FORM_FLOW_CONTROL.SIGN_IN_FORM: {
          await cancelOtp(e, setLoading); // or cancelResetFlow
          return;
        }
        case FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED: {
          onMemberCancelled?.(); // tell parent
          return closeHost();
        }
        case FORM_FLOW_CONTROL.MEMBER_SOURCE_SKIT_UPLOAD_PERMISSIONS: {
          onMemberCancelled?.(); // tell parent
          return closeHost();
        }
        case FORM_FLOW_CONTROL.SECURITY_CHECK: {
          setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
          setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
          return cancelOtp(e, setLoading);
        }

        case FORM_FLOW_CONTROL.SIGN_UP_FORM:
        default: {
          await cancelSignupFlow(e, setLoading);
          return;
        }
      }
    } catch (err) {
      // optional: log or surface an error toast
      console.error("handleContextAwareCancel error:", err);
    }
  };
  console.log("[AuthorizationForm:render switch]", {
    step: currentFormStep,
    ctx: currentContextType,
    uid: getCurrentUserId?.(),
    at: new Date().toISOString(),
  });

  // wrapped in a helper to render the appropriate child component
  return (
    <div>
      {currentFormStep === FORM_FLOW_CONTROL.SIGN_IN_FORM && (
        <LoginForm
          onLoginSuccess={handleLoginSuccess}
          onSignUpClick={() => {
            // new user starts the signup flow
            setCurrentContextType(FORM_FLOW_CONTROL.SIGN_UP_FORM);
            setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_UP_FORM);
          }}
          onResetPasswordClick={() => {
            // go to phone number field form, collect the phone number and go through the steps
            setCurrentContextType(FORM_FLOW_CONTROL.RESET_PASSWORD_FORM);
            setCurrentFormStep(FORM_FLOW_CONTROL.ACCOUNT_VERIFICATION_FORM);
          }}
        />
      )}
      {currentFormStep === FORM_FLOW_CONTROL.SIGN_UP_FORM && (
        <SignupForm
          onSignup={handleSignupUser}
          acctPhoneNumberValue={acctPhoneNumberValue}
          setAcctPhoneNumberValue={setAcctPhoneNumberValue}
          onCancel={cancelSignupFlow}
        />
      )}
      {currentFormStep === FORM_FLOW_CONTROL.VERIFY_ACCOUNT_FORM && (
        <OtpPhoneVerificationForm
          userId={getCurrentUserId()}
          currentContextType={currentContextType}
          onMobileVerification={(userId, otpCode) =>
            handleMobileVerification(userId, otpCode, {
              passingUserData: currentMemberData,
              currentContextType,
            })
          }
          onRequestResend={handleResend}
          // for now usre can skip the phone verification after 1 minute count down, (work around incase the user did not get the otp for some reason)
          onSkipVerification={performSkipVerification}
          onCancel={handleContextAwareCancel}
        />
      )}
      {currentFormStep === FORM_FLOW_CONTROL.ACCOUNT_VERIFICATION_FORM && (
        <AccountVerificationPhoneNumberFieldForm
          acctPhoneNumberValue={acctPhoneNumberValue}
          setAcctPhoneNumberValue={setAcctPhoneNumberValue}
          acctPhoneNumberBackendError={acctPhoneNumberBackendError}
          setAcctPhoneNumberBackendError={setAcctPhoneNumberBackendError}
          onCancel={
            currentContextType === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM
              ? cancelOtp
              : cancelSignupFlow
          }
          onVerifyUserAccount={handleAccountVerification}
          currentContextType={currentContextType}
        />
      )}
      {currentFormStep === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM && (
        <ResetPasswordForm
          userId={getCurrentUserId()}
          onUpdateAccount={autoSignIn}
          onCancel={cancelOtp}
        />
      )}
      {currentFormStep === FORM_FLOW_CONTROL.BUILD_PROFILE_FORM && (
        <BuildProfileSignupForm
          userId={getCurrentUserId()}
          forcePhoto={currentContextType === FORM_FLOW_CONTROL.SIGN_UP_FORM} // data validation, since the source of truth for signup is null
          currentMemberData={currentMemberData} // call back prop to determine the source of truth, which
          currentContextType={currentContextType} // used for data validation, since we may get data from the backend the source of truth for errors will be the currentMemberData
          onCompleteProfile={(updatedUser, currentContextType) => {
            // passing from the child
            setCurrentMemberData(updatedUser); // setting state to update the parent
            setCurrentMemberId(updatedUser.id); // setting state to update the parent
            completeSignup(updatedUser, currentContextType); // to the parent here
          }}
          onCancel={
            currentContextType === FORM_FLOW_CONTROL.RESET_PASSWORD_FORM
              ? cancelOtp
              : cancelSignupFlow
          }
        />
      )}
    </div>
  );
};

export default React.memo(AuthorizationForm);
